<template>
  <p
    v-if="!memory.canSave"
    class="ai-suggest__sent"
  >
    <ai-icon
      name="memory"
      :size="14"
    />
    Предложение передано администраторам
  </p>
  <div
    v-else
    class="ai-suggest"
  >
    <div class="ai-suggest__title">
      <ai-icon
        name="memory"
        :size="15"
      />
      Сохранить в память?
    </div>
    <ul class="ai-suggest__list">
      <li
        v-for="suggestion in suggestions"
        :key="suggestion.id"
        class="ai-suggest__item"
      >
        <template v-if="editing === suggestion.id">
          <textarea
            ref="draft"
            v-model="draft"
            class="ai-input"
            rows="2"
            :maxlength="maxText"
            aria-label="Текст записи"
          />
          <div class="ai-suggest__actions">
            <span class="ai-suggest__counter">{{ draft.length }}/{{ maxText }}</span>
            <button
              type="button"
              class="ai-button is-small"
              :disabled="busy === suggestion.id"
              @click="editing = null"
            >
              Отмена
            </button>
            <button
              type="button"
              class="ai-button is-small is-primary"
              :disabled="busy === suggestion.id || !draft.trim()"
              @click="saveEdited(suggestion)"
            >
              <span
                v-if="busy === suggestion.id"
                class="ai-spinner ai-spinner--small"
              />
              {{ busy === suggestion.id ? 'Проверяю…' : 'Сохранить' }}
            </button>
          </div>
        </template>
        <template v-else>
          <p class="ai-suggest__text">
            <span
              v-if="suggestion.subject"
              class="ai-suggest__subject"
            >{{ suggestion.subject }}:</span>
            {{ textOf(suggestion) }}
          </p>
          <p
            v-if="states[suggestion.id]"
            class="ai-suggest__state"
            :class="`is-${states[suggestion.id].tone}`"
          >
            <ai-icon
              :name="states[suggestion.id].icon"
              :size="14"
            />
            <span>{{ states[suggestion.id].text }}</span>
            <button
              v-if="states[suggestion.id].resolve"
              type="button"
              class="ai-link-button"
              @click="decide(suggestion, items[suggestion.id])"
            >
              Решить
            </button>
          </p>
          <div
            v-else
            class="ai-suggest__actions"
          >
            <span
              v-if="busy === suggestion.id"
              class="ai-suggest__busy"
            >
              <span class="ai-spinner ai-spinner--small" />
              {{ busyText }}
            </span>
            <button
              type="button"
              class="ai-button is-small is-primary"
              :disabled="!!busy"
              @click="approve(suggestion)"
            >
              Сохранить
            </button>
            <button
              type="button"
              class="ai-button is-small"
              :disabled="!!busy"
              @click="startEdit(suggestion)"
            >
              Изменить
            </button>
            <button
              type="button"
              class="ai-button is-small is-ghost"
              :disabled="!!busy"
              @click="reject(suggestion)"
            >
              Не сохранять
            </button>
          </div>
        </template>
        <p
          v-if="errors[suggestion.id]"
          class="ai-suggest__error"
        >
          {{ errors[suggestion.id] }}
        </p>
      </li>
    </ul>
  </div>
</template>

<script>
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import { MAX_TEXT, hasConflict } from './memory';

  const STATES = {
    saved: { tone: 'success', icon: 'check', text: 'Сохранено в память' },
    unchecked: { tone: 'success', icon: 'check', text: 'Сохранено. Противоречия не проверены: модель недоступна' },
    merged: { tone: 'success', icon: 'check', text: 'Объединено с записью в памяти' },
    rejected: { tone: 'muted', icon: 'close', text: 'Не сохранено' },
    duplicate: { tone: 'muted', icon: 'info', text: 'Такая запись уже есть в памяти' },
    waiting: { tone: 'warning', icon: 'alert', text: 'Ждёт решения: расходится с записью в памяти', resolve: true },
  };
  const RESOLVED = { replace: 'saved', keep_both: 'saved', merge: 'merged', discard: 'rejected' };

  // «Запомни…» in a chat answer: the administrator saves, edits or rejects the suggested entries right here
  // (other users see that the suggestion went to the administrators). The card state is taken from
  // the memory itself, so it is right after a reload too.
  export default {
    name: 'MemorySuggestions',
    components: { AiIcon },
    inject: { aiMemory: { default: null } },
    props: {
      // meta.memory of an answer: { suggestions: [{id, text, subject}], canSave }.
      memory: { type: Object, required: true },
    },
    data: () => ({
      states: {},
      items: {},
      errors: {},
      busy: '',
      busyText: '',
      editing: null,
      draft: '',
      maxText: MAX_TEXT,
    }),
    computed: {
      suggestions() {
        return this.memory.suggestions || [];
      },
    },
    created() {
      if (this.memory.canSave) this.sync();
    },
    methods: {
      textOf(suggestion) {
        const item = this.items[suggestion.id];
        return item ? item.text : suggestion.text;
      },
      setState(id, key) {
        this.$set(this.states, id, key ? STATES[key] : null);
      },
      remember(item) {
        if (item) this.$set(this.items, item.id, item);
      },
      // Active and pending entries: what is not among them was rejected, archived or deleted.
      async sync() {
        try {
          const { data } = await api.listMemory();
          const known = {};
          (data.items || []).forEach((item) => {
            known[item.id] = item;
          });
          this.suggestions.forEach(({ id }) => {
            const item = known[id];
            this.remember(item);
            if (!item) this.setState(id, 'rejected');
            else if (item.status === 'active') this.setState(id, 'saved');
            else if (hasConflict(item)) this.setState(id, 'waiting');
          });
        } catch (error) {
          // Without the memory list the buttons are shown as they are.
        }
      },
      changed() {
        this.$store.dispatch('assistant/loadMemoryCounts');
      },
      // Saving checks the entry against the memory (a model call); rejecting does not.
      async run(suggestion, job, text = 'Проверяю…') {
        this.busy = suggestion.id;
        this.busyText = text;
        this.$set(this.errors, suggestion.id, '');
        try {
          return await job();
        } catch (error) {
          this.$set(this.errors, suggestion.id, api.errorText(error, 'Не удалось сохранить'));
          return false;
        } finally {
          this.busy = '';
          this.changed();
        }
      },
      // An approve or edit answer: saved, a duplicate, or a conflict for the dialog.
      async settle(suggestion, data) {
        const { item, check } = data;
        this.remember(item);
        if (check && check.verdict === 'duplicate') {
          this.setState(suggestion.id, 'duplicate');
        } else if (hasConflict(item)) {
          await this.decide(suggestion, item);
        } else if (item.status === 'active') {
          this.setState(suggestion.id, check && check.verdict === 'unchecked' ? 'unchecked' : 'saved');
        }
      },
      async decide(suggestion, item) {
        if (!this.aiMemory || !item) return;
        const result = await this.aiMemory.resolve(item);
        if (result) {
          this.remember(result.item);
          this.setState(suggestion.id, RESOLVED[result.action]);
        } else {
          this.setState(suggestion.id, 'waiting');
        }
        this.changed();
      },
      approve(suggestion) {
        return this.run(suggestion, async () => {
          const { data } = await api.approveMemory(suggestion.id);
          await this.settle(suggestion, data);
        });
      },
      reject(suggestion) {
        return this.run(suggestion, async () => {
          const { data } = await api.rejectMemory(suggestion.id);
          this.remember(data.item);
          this.setState(suggestion.id, 'rejected');
        }, 'Сохраняю…');
      },
      startEdit(suggestion) {
        this.draft = this.textOf(suggestion);
        this.editing = suggestion.id;
        this.$nextTick(() => {
          const input = this.$refs.draft && this.$refs.draft[0];
          if (input) input.focus();
        });
      },
      // The edited text is checked when it is saved; without a conflict the suggestion is approved.
      saveEdited(suggestion) {
        return this.run(suggestion, async () => {
          const text = this.draft.trim();
          let data;
          if (text !== this.textOf(suggestion)) {
            ({ data } = await api.updateMemory(suggestion.id, { text }));
            if (data.check && data.check.verdict === 'duplicate') {
              this.$set(this.errors, suggestion.id, 'Такая запись уже есть в памяти');
              return;
            }
            this.remember(data.item);
          }
          if (!data || !hasConflict(data.item)) ({ data } = await api.approveMemory(suggestion.id));
          this.editing = null;
          await this.settle(suggestion, data);
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-suggest {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
    padding: 10px 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);
    background: var(--ui-surface-soft);
  }

  .ai-suggest__title {
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--ui-text);
    font-size: 13px;
    font-weight: 500;

    .ai-icon {
      color: var(--ui-primary);
    }
  }

  .ai-suggest__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .ai-suggest__item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 10px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface);
  }

  .ai-suggest__text {
    margin: 0;
    color: var(--ui-text);
    font-size: 13px;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  .ai-suggest__subject {
    color: var(--ui-text-muted);
    font-weight: 500;
  }

  .ai-suggest__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
  }

  .ai-suggest__busy,
  .ai-suggest__counter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-right: auto;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-suggest__state {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 12px;

    &.is-success .ai-icon {
      color: var(--ui-success);
    }

    &.is-warning .ai-icon {
      color: var(--ui-warning);
    }
  }

  .ai-suggest__error {
    margin: 0;
    color: var(--ui-danger);
    font-size: 12px;
  }

  .ai-suggest__sent {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 8px 0 0;
    color: var(--ui-text-muted);
    font-size: 12px;
  }
</style>
